/**
 * Evolution View - Pattern evolution controls and monitoring
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dna,
  Play,
  Pause,
  RefreshCw,
  TrendingUp,
  Check,
  Database,
  Zap,
  Target,
  ArrowRight,
  Brain,
  Sparkles,
  Activity,
  BarChart3,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { convexService } from "@/services/convexService";
import type { Pattern, DiscoveredPattern } from "@/services/convexService";
import * as Tabs from "@radix-ui/react-tabs";

export function EvolutionView() {
  const [isRunning, setIsRunning] = useState(false);
  const [patterns, setPatterns] = useState<Pattern[]>([]);
  const [discoveredPatterns, setDiscoveredPatterns] = useState<DiscoveredPattern[]>([]);
  const [stats, setStats] = useState({
    totalGenerations: 0,
    highFitness: 0,
    avgFitness: 0,
    mutationsCount: 0,
  });

  useEffect(() => {
    refreshData();
  }, []);

  const refreshData = async () => {
    try {
      const [patternsList, discoveredList] = await Promise.all([
        convexService.listPatterns({ limit: 100 }),
        convexService.getDiscoveredPatterns({ minConfidence: 0.5 }),
      ]);

      setPatterns(patternsList);
      setDiscoveredPatterns(discoveredList);

      if (patternsList.length > 0) {
        const confidences = patternsList.map(p => p.confidence);
        const maxGen = Math.max(...patternsList.map(p => p.evolution?.generation ?? 1));

        setStats({
          totalGenerations: maxGen,
          highFitness: Math.max(...confidences) * 100,
          avgFitness: (confidences.reduce((a, b) => a + b, 0) / confidences.length) * 100,
          mutationsCount: patternsList.reduce((sum, p) => sum + (p.evolution?.mutations.length ?? 0), 0),
        });
      }
    } catch (error) {
      console.error("Failed to fetch evolution data:", error);
    }
  };

  const toggleEvolution = () => {
    setIsRunning(!isRunning);
  };

  const promotePattern = async (patternId: string, operatorIds: string[]) => {
    try {
      await convexService.promotePattern(patternId, operatorIds);
      await refreshData();
    } catch (error) {
      console.error("Failed to promote pattern:", error);
    }
  };

  const rejectPattern = async (patternId: string) => {
    try {
      await convexService.rejectPattern(patternId);
      await refreshData();
    } catch (error) {
      console.error("Failed to reject pattern:", error);
    }
  };

  const elitePatterns = patterns
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, 10);

  const highConfidenceDiscovered = discoveredPatterns
    .filter(p => p.confidence >= 0.7)
    .sort((a, b) => b.confidence - a.confidence);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Pattern Evolution</h1>
          <p className="text-muted-foreground mt-1">
            Genetic optimization and pattern fitness tracking
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={refreshData}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-transparent border border-[hsl(var(--border))] rounded-md hover:bg-[hsl(var(--muted))] transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            onClick={toggleEvolution}
            className={cn(
              "flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors",
              isRunning
                ? "bg-red-500 text-white hover:bg-red-600"
                : "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90"
            )}
          >
            {isRunning ? (
              <>
                <Pause className="h-4 w-4" />
                Pause Evolution
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                Start Evolution
              </>
            )}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatsCard
          title="Total Generations"
          value={stats.totalGenerations.toString()}
          icon={Dna}
          color="text-purple-500"
        />
        <StatsCard
          title="Highest Fitness"
          value={`${stats.highFitness.toFixed(0)}%`}
          icon={TrendingUp}
          color="text-green-500"
        />
        <StatsCard
          title="Average Fitness"
          value={`${stats.avgFitness.toFixed(1)}%`}
          icon={BarChart3}
          color="text-blue-500"
        />
        <StatsCard
          title="Total Mutations"
          value={stats.mutationsCount.toString()}
          icon={Zap}
          color="text-orange-500"
        />
      </div>

      {/* Content Tabs */}
      <Tabs.Root defaultValue="elite" className="w-full">
        <Tabs.List className="inline-flex h-10 items-center justify-center rounded-md bg-[hsl(var(--muted))] p-1 w-full">
          <Tabs.Trigger
            value="elite"
            className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm flex-1"
          >
            Elite Patterns
          </Tabs.Trigger>
          <Tabs.Trigger
            value="discovered"
            className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm flex-1"
          >
            Discovered
          </Tabs.Trigger>
          <Tabs.Trigger
            value="generation"
            className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm flex-1"
          >
            Generations
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="elite" className="mt-4 space-y-4">
          <div className="h-[500px] overflow-y-auto pr-2 space-y-3">
            <AnimatePresence>
              {elitePatterns.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Dna className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No patterns evolved yet</p>
                </div>
              ) : (
                elitePatterns.map((pattern) => (
                  <EvolutionPatternCard key={pattern._id} pattern={pattern} />
                ))
              )}
            </AnimatePresence>
          </div>
        </Tabs.Content>

        <Tabs.Content value="discovered" className="mt-4 space-y-4">
          <div className="h-[500px] overflow-y-auto pr-2 space-y-3">
            <AnimatePresence>
              {highConfidenceDiscovered.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No high-confidence patterns discovered</p>
                </div>
              ) : (
                highConfidenceDiscovered.map((pattern) => (
                  <DiscoveredPatternCard
                    key={pattern._id}
                    pattern={pattern}
                    onPromote={promotePattern}
                    onReject={rejectPattern}
                  />
                ))
              )}
            </AnimatePresence>
          </div>
        </Tabs.Content>

        <Tabs.Content value="generation" className="mt-4 space-y-4">
          <div className="text-center py-12 text-muted-foreground">
            <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Generation tracking visualization</p>
            <p className="text-sm mt-2">
              This will show fitness trends across generations as patterns evolve
            </p>
          </div>
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
}

// Stats Card Component
function StatsCard({
  title,
  value,
  icon: Icon,
  color,
}: {
  title: string;
  value: string;
  icon: any;
  color: string;
}) {
  return (
    <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-card-foreground shadow-sm">
      <div className="p-6 pb-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-muted-foreground">
            {title}
          </div>
          <Icon className={`h-4 w-4 ${color}`} />
        </div>
      </div>
      <div className="p-6 pt-3">
        <div className="text-2xl font-bold">{value}</div>
      </div>
    </div>
  );
}

// Evolution Pattern Card Component
function EvolutionPatternCard({ pattern }: { pattern: Pattern }) {
  const fitness = Math.round(pattern.confidence * 100);
  const generation = pattern.evolution?.generation ?? 1;
  const mutations = pattern.evolution?.mutations ?? [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-card-foreground shadow-sm">
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-lg font-semibold">
                <Brain className="h-4 w-4 text-purple-500 flex-shrink-0" />
                <span className="truncate">{pattern.name}</span>
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                Generation {generation} • {pattern.executionCount} executions
              </div>
            </div>
            <span className={cn(
              "px-2 py-1 text-xs rounded",
              fitness >= 80 ? "bg-[hsl(var(--primary))] text-white" : fitness >= 60 ? "bg-[hsl(var(--muted))] text-muted-foreground" : "border border-[hsl(var(--border))]"
            )}>
              {fitness}% fitness
            </span>
          </div>
        </div>

        <div className="p-6 pt-0 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Success Rate</span>
            <span className="font-medium">
              {pattern.steps.length > 0
                ? ((pattern.successCount / Math.max(pattern.executionCount, 1)) * 100).toFixed(0) + "%"
                : "N/A"}
            </span>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Pattern Confidence</span>
            <span className="font-medium">{(pattern.confidence * 100).toFixed(0)}%</span>
          </div>

          {mutations.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-2">Recent Mutations</div>
              <div className="space-y-1">
                {mutations.slice(0, 3).map((mutation, i) => (
                  <div key={i} className="text-xs text-muted-foreground">
                    • {mutation}
                  </div>
                ))}
                {mutations.length > 3 && (
                  <div className="text-xs text-muted-foreground">
                    +{mutations.length - 3} more
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 pt-2">
            <span className="px-2 py-0.5 text-xs bg-[hsl(var(--muted))] rounded">
              {pattern.steps.length} steps
            </span>
            <span className="px-2 py-0.5 text-xs bg-[hsl(var(--muted))] rounded">
              {(pattern.decay * 100).toFixed(0)}% decay
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Discovered Pattern Card Component
function DiscoveredPatternCard({
  pattern,
  onPromote,
  onReject,
}: {
  pattern: DiscoveredPattern;
  onPromote: (id: string, operatorIds: string[]) => void;
  onReject: (id: string) => void;
}) {
  const confidence = Math.round(pattern.confidence * 100);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-card-foreground shadow-sm">
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-lg font-semibold">
                <Sparkles className="h-4 w-4 text-green-500 flex-shrink-0" />
                <span className="truncate">{pattern.intent}</span>
              </div>
              <div className="text-sm text-muted-foreground mt-1 truncate">
                From {pattern.source} • Discovered {new Date(pattern.discoveredAt).toLocaleDateString()}
              </div>
            </div>
            <span className={cn(
              "px-2 py-1 text-xs rounded",
              pattern.status === "pending" ? "bg-[hsl(var(--muted))] text-muted-foreground" : "border border-[hsl(var(--border))]"
            )}>
              {pattern.status}
            </span>
          </div>
        </div>

        <div className="p-6 pt-0 space-y-3">
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-1">Confidence</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-[hsl(var(--muted))] rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500"
                  style={{ width: `${confidence}%` }}
                />
              </div>
              <span className="text-sm font-medium">{confidence}%</span>
            </div>
          </div>

          {pattern.suggestedOperators.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-2">Suggested Operators</div>
              <div className="flex flex-wrap gap-1">
                {pattern.suggestedOperators.slice(0, 4).map((op, i) => (
                  <span key={i} className="px-2 py-0.5 text-xs border border-[hsl(var(--border))] rounded">
                    {op}
                  </span>
                ))}
                {pattern.suggestedOperators.length > 4 && (
                  <span className="px-2 py-0.5 text-xs border border-[hsl(var(--border))] rounded">
                    +{pattern.suggestedOperators.length - 4}
                  </span>
                )}
              </div>
            </div>
          )}

          {pattern.triggerPhrases.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-2">Trigger Phrases</div>
              <div className="flex flex-wrap gap-1">
                {pattern.triggerPhrases.slice(0, 3).map((phrase, i) => (
                  <span key={i} className="px-2 py-0.5 text-xs bg-[hsl(var(--muted))] rounded">
                    {phrase}
                  </span>
                ))}
                {pattern.triggerPhrases.length > 3 && (
                  <span className="px-2 py-0.5 text-xs bg-[hsl(var(--muted))] rounded">
                    +{pattern.triggerPhrases.length - 3}
                  </span>
                )}
              </div>
            </div>
          )}

          {pattern.sourceUrl && (
            <a
              href={pattern.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline text-sm flex items-center gap-1"
            >
              <span className="truncate">{pattern.sourceUrl}</span>
              <ArrowRight className="h-3 w-3 flex-shrink-0" />
            </a>
          )}

          <div className="flex gap-2 pt-2">
            <button
              onClick={() => onPromote(pattern._id, pattern.suggestedOperators)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-[hsl(var(--primary))] text-white rounded hover:opacity-90"
            >
              <Target className="h-3 w-3" />
              Promote
            </button>
            <button
              onClick={() => onReject(pattern._id)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-[hsl(var(--border))] rounded hover:bg-[hsl(var(--muted))]"
            >
              Reject
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
